import { css, jsx } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { MOBILE_WIDTH } from "../../../../utils/HelperStyles";
import IconBerserkerMail from "../../../Icon";
import StyledButton from "../../../StyledButton";
import { StyledRadio } from "../../../StyledRadio";
import { useModal } from "../../Modal";
import { MdClose } from "react-icons/md";
import { Checkbox } from "@chakra-ui/react";

const ModalContainer = styled.div<{ isModal?: boolean }>`
  height: ${(props) => (props.isModal ? "100%" : "74vh")};
  width: 56vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  border-radius: 25px;
  padding: 0 0 50px;

  position: relative;

  .scroll-flag {
    position: absolute;
    top: 7px;
    left: -4px;
  }

  .broken-icon-1 {
    margin-top: 12px;
  }

  .broken-icon-2 {
    margin-top: 17px;
  }

  @media (max-width: ${MOBILE_WIDTH}) {
    width: 100vw;
    height: 100vh;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;

  padding: 16px 0;

  border-bottom: 1px solid #eceeef;

  svg {
    padding: 24px 24px 0;
  }
`;

const FollowingContent = styled.div`
  text-align: left;
  font: normal normal normal 20px/27px Lato;
  letter-spacing: 0px;
  color: #111111;

  padding-top: 25px;
`;

const FollowingContentItems = styled.div<{ bold?: boolean }>`
  text-align: left;
  font: normal normal ${(props) => (props.bold ? "bold" : "normal")} 20px/30px
    Lato;
  letter-spacing: 0px;
  color: #111111;

  padding-bottom: 20px;

  display: flex;
  align-items: flex-start;

  :first-child {
    padding-top: 30px;
  }

  > div {
    margin-top: 0.5rem;
    margin-right: 9.5px;
  }
`;

const Underline = styled.u`
  display: contents;

  @supports (text-underline-offset: 0.2rem) {
    text-underline-offset: 0.2rem;
  }
`;

const Acknowledge = styled.div`
  width: 100%;
  height: 74px;

  display: flex;
  align-items: center;
  justify-content: start;

  background-color: #fff0e0;

  padding: 20px 32px;
  border-radius: 8px;
  margin-bottom: 24px;

  .text {
    font: normal normal bold 19px Lato;
    color: #111111;
  }

  cursor: pointer;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  /* iOS Safari */
  /* Safari */
  /* Konqueror HTML */
  /* Old versions of Firefox */
  /* Internet Explorer/Edge */
  /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
`;

const FollowingWrapper = styled.div<{ isModal?: boolean }>`
  display: flex;
  flex-direction: column;

  overflow-y: ${(props) => (props.isModal ? "scroll" : "auto")};

  padding: 0px 60px;

  @media (max-width: ${MOBILE_WIDTH}) {
    padding: 0px 25px;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;

  padding: 8px;

  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    color: #868482;
  }
`;

interface Props {
  onAgree: () => void;
  isModal?: boolean;
  onCancel: () => void;
}

export default function PreBuy(props: Props): JSX.Element {
  const { onAgree, isModal, onCancel } = props;
  const modal = useModal();
  const [proceedCheck, setProceedCheck] = useState<boolean>(false);

  return (
    <ModalContainer isModal={isModal}>
      <img
        className="scroll-flag"
        src="/images/scroll-modal-1.png"
        alt="Scroll"
      />

      <CloseButtonWrapper onClick={onCancel}>
        <MdClose />
      </CloseButtonWrapper>

      <HeaderContainer>
        <IconBerserkerMail name="stop-logo" width={450} />
      </HeaderContainer>
      <FollowingWrapper isModal={isModal}>
        <FollowingContent>
          Before you sign up for BerserkerMail, realize the following:
          <FollowingContentItems bold>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            This is a first release, and while everything described on the sales
            page is working, some features are still in the “bare bones” stage,
            with more enhancements on the way
          </FollowingContentItems>
          <FollowingContentItems>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            These first few releases are <Underline>guaranteed</Underline> to
            have a lot of bugs, kinks, and problems — you can count on it
          </FollowingContentItems>
          <FollowingContentItems bold>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            If you lack patience, need constant hand-holding, or are the
            annoying type who will relentlessly bombard the help desk every time
            there is a hiccup, demanding instant fixes as we get all the bugs
            worked out… do <Underline>NOT</Underline> buy yet
          </FollowingContentItems>
          <FollowingContentItems>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            In fact, realize{" "}
            <Underline>
              the help desk is not even yet trained on how to handle every
              problem
            </Underline>
            , which means some bugs and fixes will take more time to iron out
            than others
          </FollowingContentItems>
          <FollowingContentItems bold>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            So what you save in money, you’ll be making up for in some
            inconvenience
          </FollowingContentItems>
          <FollowingContentItems>
            <IconBerserkerMail name="check-bold" color="#5FA200" />
            As much as we’d love to have thousands of clients right away, it’s
            best to grow a software company slowly & gradually — and the worst
            thing would be to have too many join during this first release
          </FollowingContentItems>
          <FollowingContentItems bold>
            <div className="broken-icon-1">
              <IconBerserkerMail name="check-bold" color="#5FA200" />
            </div>
            <div>
              <span style={{ backgroundColor: "yellow" }}>
                There are rules in place that may seem inconvenient, but that
                are part of the process of ensuring the highest inbox delivery
                we can help you get.
              </span>{" "}
              Two of them you should be aware of before signing up are (1) we
              don’t allow images in emails and (2) you are limited to just 3
              URLs in an email (whether they are the same URL or different from
              each other).
            </div>
          </FollowingContentItems>
          <FollowingContentItems>
            <div className="broken-icon-2">
              <IconBerserkerMail name="check-bold" color="#5FA200" />
            </div>
            <div>
              During the first few releases especially{" "}
              <span style={{ backgroundColor: "yellow", fontWeight: 600 }}>
                we WILL lock out & permanently ban any “prima donna” customers
                who lack patience and are constantly harassing the help desk,
              </span>{" "}
              unable to wait for certain bugs to be fixed
            </div>
          </FollowingContentItems>
          <FollowingContentItems>
            <div className="broken-icon-2">
              <IconBerserkerMail name="check-bold" color="#5FA200" />
            </div>
            <div>
              The campaign builder is currently in beta. This means that while
              the functionality is there, you may still experience some bugs.
              This is especially inconvenient right now when it comes to editing
              campaigns.{" "}
              <span style={{ backgroundColor: "yellow", fontWeight: 600 }}>
                It is NOT recommended that you create big massive campaigns at
                this point. Please start your usage and testing with small,
                simple campaigns until you are comfortable with how the Campaign
                Builder works and its current limitations.
              </span>
            </div>
          </FollowingContentItems>
          <FollowingContentItems>
            <div className="broken-icon-2">
              <IconBerserkerMail name="check-bold" color="#5FA200" />
            </div>
            <div>
              There are no refunds on our software services nor on any of the
              upsells or digital training products you may or may not purchase.{" "}
              <span style={{ backgroundColor: "yellow", fontWeight: 600 }}>
                NO REFUNDS PERIOD.
              </span>
            </div>
          </FollowingContentItems>
        </FollowingContent>
        <FollowingContent>
          Bottom line:
          <FollowingContentItems>
            Yes, by getting in today you’ll be saving a lot of money per month
            in the short term, hundreds of dollars per month in the future (as
            the price will be going up over time for future clients), and
            thousands — if not tens of thousands — of dollars for the rest of
            your life over time.
          </FollowingContentItems>
          <FollowingContentItems>
            But there will be some problems during the first few releases.
          </FollowingContentItems>
          <FollowingContentItems>
            Realize that <Underline>before</Underline> going in.
          </FollowingContentItems>
          <FollowingContentItems bold>
            <Underline>
              If that is going to be an issue for you, then do not buy.
            </Underline>
          </FollowingContentItems>
          <FollowingContentItems>
            Simply wait until later when there are less bugs.
          </FollowingContentItems>
          <FollowingContentItems>
            If you still want in knowing that, check this box to acknowledge the
            above:
          </FollowingContentItems>
        </FollowingContent>
        <Acknowledge onClick={() => setProceedCheck((prevState) => !prevState)}>
          <Checkbox
            _focus={{ outline: "none" }}
            bgColor="#fff"
            size="lg"
            colorScheme="orange"
            isChecked={proceedCheck}
            onChange={() => setProceedCheck((prevState) => !prevState)}
            mr="16px"
            css={css`
              .css-17f88hv[aria-checked="true"],
              .css-17f88hv[data-checked] {
                background: var(--chakra-colors-orange-50) !important;
                border-color: var(--chakra-colors-orange-50) !important;
              }
            `}
          />
          <div className="text">
            Yes, I agree {"&"} understand there are no refunds on this purchase.
          </div>
        </Acknowledge>
        <div
          style={{
            marginBottom: "20px",
            width: "100%",
            borderTop: "1px solid #eceeef",
            justifyContent: "flex-end",
            display: "flex",
          }}
        >
          <StyledButton
            disable={!proceedCheck}
            style={{
              height: "46px",
              width: "210px",
              // borderRadius: "23px",
              marginTop: "30px",
              padding: "12px 21px",
            }}
            onClick={onAgree}
          >
            Proceed to Checkout
          </StyledButton>
        </div>
      </FollowingWrapper>
    </ModalContainer>
  );
}
